import classnames from 'classnames';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import ScrollContainer from 'react-indiana-drag-scroll';
import Hexagon from '../../Hexagon/Hexagon';
import ChannelThumbnail from '../ChannelThumbnail/ChannelThumbnail';
import { ListTitle } from '../ItemList/ListTitle';

import './ChannelListHexagon.scss';
import { ChevronIcon, ChevronIconDirection } from './ChevronIcon/ChevronIcon';

const Placeholder = () => (
	<Hexagon className="ChannelThumbnail" size="none">
		<div style={{ background: 'black', opacity: 0.1, width: '100%', height: '100%' }} />
	</Hexagon>
);

export const ChannelListHexagon = ({
	channels,
	className,
	forwardedRef,
	placeholderLength,
	title,
}) => {
	const scrollContainer = useRef();
	const [isLoaded, setIsLoaded] = useState(false);

	const [isScrollable, setIsScrollable] = useState(
		{ right: true, left: true },
	);

	// Because placeholder array has a length of 50, on dev enrivonment where there is
	// less than 50 channels, the scroll position won't be moved to the right place
	const channelsOrPlaceholder = channels?.length > 0
		? channels
		: Array.from({ length: placeholderLength });

	const checkScroll = useCallback(() => {
		if (scrollContainer.current) {
			setIsScrollable({
				left: scrollContainer.current.scrollLeft > 0,
				right: (scrollContainer.current.scrollLeft + scrollContainer.current.clientWidth)
					< scrollContainer.current.scrollWidth,
			});
		}
	}, []);

	useLayoutEffect(() => {
		window.addEventListener('resize', checkScroll);
		checkScroll();
		return () => window.removeEventListener('resize', checkScroll);
	}, [checkScroll]);

	useLayoutEffect(() => {
		if (scrollContainer.current) {
			scrollContainer.current.scrollLeft = (scrollContainer.current.scrollWidth / 2)
			- (scrollContainer.current.clientWidth / 2);
			setIsLoaded(true);
		}
	}, []);

	const handleHorizontalScrollRight = () => {
		scrollContainer.current.scrollLeft += (scrollContainer.current.clientWidth);
	};

	const handleHorizontalScrollLeft = () => {
		scrollContainer.current.scrollLeft += (-scrollContainer.current.clientWidth);
	};

	return (
		<section
			className={classnames('ChannelList ChannelListHexagon size-lg', className)}
			ref={forwardedRef}
		>
			{title && <span className="d-inline-block ml-5"><ListTitle title={title} /></span>}
			<ScrollContainer
				className={clsx('ChannelListHexagon__scrollContainer content-white', { isLoaded })}
				innerRef={scrollContainer}
				onEndScroll={checkScroll}
				vertical={false}
				key="ChannelScroll"
			>
				<div className="ChannelListHexagon_list">
					{
						channelsOrPlaceholder.map((channel, index) => (
							channel ? (
								<ChannelThumbnail
									channel={channel}
									key={channel.hashtag}
								/>
							) : <Placeholder key={index} />
						))
					}
				</div>
			</ScrollContainer>
			{
				isScrollable.left && (
					<span
						className="ChannelList_chevron_left pl-3 color-yellow"
						onClick={handleHorizontalScrollLeft}
					>
						<div className="position-absolute start-100 translate-middle">
							<ChevronIcon />
						</div>
					</span>
				)
			}
			{
				isScrollable.right && (
					<span
						className="ChannelList_chevron_right pr-3 color-yellow"
						onClick={handleHorizontalScrollRight}
					>
						<div className="position-absolute translate-middle">
							<ChevronIcon direction={ChevronIconDirection.RIGHT} />
						</div>
					</span>
				)
			}
		</section>
	);
};

ChannelListHexagon.propTypes = {
	channels: PropTypes.arrayOf(ChannelThumbnail.propTypes.channel),
	className: PropTypes.string,
	forwardedRef: PropTypes.shape({}),
	placeholderLength: PropTypes.number,
	title: PropTypes.node,
};

ChannelListHexagon.defaultProps = {
	channels: [],
	className: '',
	forwardedRef: null,
	placeholderLength: 50,
	title: null,
};
